import { Box, Button, IconButton, Typography, useTheme, alpha, TextField, MenuItem } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useCallback } from "react";
import { StatementData, GenericStatementData, WagesStatementData } from "../../../../interface/extracted";
import { CashFlowCategory } from "../../../../interface/extracted/statements/default";
import FormField from "./FormField";
import FormTextField from "./FormTextField";

interface GenericDataItem {
    key: string;
    name: string;
    value: number;
    cashFlowCategory: CashFlowCategory;
}

const CASH_FLOW_CATEGORY_OPTIONS: { value: CashFlowCategory; label: string }[] = [
    { value: "unclassified", label: "Unclassified" },
    { value: "operating", label: "Operating" },
    { value: "depreciation", label: "Depreciation" },
    { value: "amortization", label: "Amortization" },
    { value: "depletion", label: "Depletion" },
    { value: "casualtyLoss", label: "Casualty Loss" },
    { value: "mortgageNotesPayableLessThanOneYear", label: "Mortgage Notes Payable < 1yr" },
    { value: "travelAndEntertainment", label: "Travel & Entertainment" },
    { value: "nonRecurringIncome", label: "Non-Recurring Income" },
    { value: "nonRecurringExpense", label: "Non-Recurring Expense" },
];

const isNonRecurring = (category: CashFlowCategory | undefined): boolean =>
    category === "nonRecurringIncome" || category === "nonRecurringExpense";

interface WagesEmployer {
    taxpayerSpouse: "T" | "S" | null;
    employerName: string;
    amountPaid: number | null;
    federalTaxWithheld: number | null;
    stateTaxWithheld: number | null;
    cityOrSdiTaxWithheld: number | null;
    ficaTax: number | null;
    medicareTax: number | null;
}

interface FormFieldStatementSectionsProps {
    title: string;
    statements: StatementData[];
    fieldPath: string;
    handleFieldChange: (fieldPath: string, value: StatementData[]) => void;
    getFieldConfidence?: (fieldPath: string) => number;
}

const emptyGenericStatement: GenericStatementData = {
    statementSubtype: "generic",
    associated: {
        statementNumber: null,
        taxpayerIdentificationNumber: undefined,
        tinType: undefined,
        name: undefined,
    },
    title: "",
    data: [],
};

const emptyWagesStatement: WagesStatementData = {
    statementSubtype: "wagesReceived",
    associated: {
        statementNumber: null,
        taxpayerIdentificationNumber: undefined,
        tinType: undefined,
        name: undefined,
    },
    title: "",
    formReference: "",
    employers: [],
    totals: {
        amountPaid: null,
        federalTaxWithheld: null,
        stateTaxWithheld: null,
        cityOrSdiTaxWithheld: null,
        ficaTax: null,
        medicareTax: null,
    },
};

const emptyDataItem: GenericDataItem = {
    key: "",
    name: "",
    value: 0,
    cashFlowCategory: "unclassified",
};

const emptyEmployer: WagesEmployer = {
    taxpayerSpouse: null,
    employerName: "",
    amountPaid: null,
    federalTaxWithheld: null,
    stateTaxWithheld: null,
    cityOrSdiTaxWithheld: null,
    ficaTax: null,
    medicareTax: null,
};

const FormFieldStatementSections = ({
    title,
    statements = [],
    fieldPath,
    handleFieldChange,
    getFieldConfidence,
}: FormFieldStatementSectionsProps) => {
    const theme = useTheme();

    const handleAddStatement = useCallback(
        (subtype: "generic" | "wagesReceived" = "generic") => {
            const newStatement =
                subtype === "wagesReceived" ? { ...emptyWagesStatement } : { ...emptyGenericStatement };
            const newStatements = [...statements, newStatement];
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const handleRemoveStatement = useCallback(
        (index: number) => {
            const newStatements = statements.filter((_: StatementData, i: number) => i !== index);
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const handleStatementFieldChange = useCallback(
        (statementIndex: number, field: string, value: unknown) => {
            const newStatements = [...statements];
            newStatements[statementIndex] = { ...newStatements[statementIndex], [field]: value } as StatementData;
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const handleAssociatedFieldChange = useCallback(
        (statementIndex: number, field: keyof StatementData["associated"], value: unknown) => {
            const newStatements = [...statements];
            newStatements[statementIndex] = {
                ...newStatements[statementIndex],
                associated: {
                    ...newStatements[statementIndex].associated,
                    [field]: value,
                },
            } as StatementData;
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    // Generic statement handlers
    const handleAddDataItem = useCallback(
        (statementIndex: number) => {
            const statement = statements[statementIndex];
            if (statement.statementSubtype !== "generic") return;
            const newStatements = [...statements];
            const newData = [...(statement.data || []), emptyDataItem];
            newStatements[statementIndex] = { ...statement, data: newData };
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const handleRemoveDataItem = useCallback(
        (statementIndex: number, dataIndex: number) => {
            const statement = statements[statementIndex];
            if (statement.statementSubtype !== "generic") return;
            const newData = statement.data.filter((_: GenericDataItem, i: number) => i !== dataIndex);
            const newStatements = [...statements];
            newStatements[statementIndex] = { ...statement, data: newData };
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const handleDataItemFieldChange = useCallback(
        (statementIndex: number, dataIndex: number, field: keyof GenericDataItem, value: unknown) => {
            const statement = statements[statementIndex];
            if (statement.statementSubtype !== "generic") return;
            const newStatements = [...statements];
            const newData = [...(statement.data || [])];
            if (!newData[dataIndex]) {
                newData[dataIndex] = { ...emptyDataItem };
            }
            newData[dataIndex] = { ...newData[dataIndex], [field]: value };
            newStatements[statementIndex] = { ...statement, data: newData };
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    // Wages statement handlers
    const handleAddEmployer = useCallback(
        (statementIndex: number) => {
            const statement = statements[statementIndex];
            if (statement.statementSubtype !== "wagesReceived") return;
            const newStatements = [...statements];
            const newEmployers = [...(statement.employers || []), { ...emptyEmployer }];
            newStatements[statementIndex] = { ...statement, employers: newEmployers };
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const handleRemoveEmployer = useCallback(
        (statementIndex: number, employerIndex: number) => {
            const statement = statements[statementIndex];
            if (statement.statementSubtype !== "wagesReceived") return;
            const newEmployers = statement.employers.filter((_: WagesEmployer, i: number) => i !== employerIndex);
            const newStatements = [...statements];
            newStatements[statementIndex] = { ...statement, employers: newEmployers };
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const handleEmployerFieldChange = useCallback(
        (statementIndex: number, employerIndex: number, field: keyof WagesEmployer, value: unknown) => {
            const statement = statements[statementIndex];
            if (statement.statementSubtype !== "wagesReceived") return;
            const newStatements = [...statements];
            const newEmployers = [...(statement.employers || [])];
            if (!newEmployers[employerIndex]) {
                newEmployers[employerIndex] = { ...emptyEmployer };
            }
            newEmployers[employerIndex] = { ...newEmployers[employerIndex], [field]: value };
            newStatements[statementIndex] = { ...statement, employers: newEmployers };
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const handleTotalsFieldChange = useCallback(
        (statementIndex: number, field: string, value: unknown) => {
            const statement = statements[statementIndex];
            if (statement.statementSubtype !== "wagesReceived") return;
            const newStatements = [...statements];
            newStatements[statementIndex] = {
                ...statement,
                totals: {
                    ...statement.totals,
                    [field]: value,
                },
            };
            handleFieldChange(fieldPath, newStatements);
        },
        [statements, fieldPath, handleFieldChange],
    );

    const getLowConfidence = (path: string): boolean => {
        if (!getFieldConfidence) return false;
        const confidence = getFieldConfidence(path);
        return confidence < 0.8;
    };

    const renderAssociatedFields = (statement: StatementData, statementIndex: number) => (
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
                <FormField
                    fieldPath={`${fieldPath}[${statementIndex}].associated.statementNumber`}
                    value={statement.associated?.statementNumber ?? ""}
                    handleFieldChange={(_, value) =>
                        handleAssociatedFieldChange(statementIndex, "statementNumber", value ? Number(value) : null)
                    }
                    label="Statement #"
                    type="number"
                    isImportant
                    lowConfidence={getLowConfidence(`${fieldPath}[${statementIndex}].associated.statementNumber`)}
                />
            </Box>
            <Box sx={{ flex: 2 }}>
                <FormTextField
                    fieldPath={`${fieldPath}[${statementIndex}].associated.taxpayerIdentificationNumber`}
                    value={statement.associated?.taxpayerIdentificationNumber || ""}
                    handleFieldChange={(_, value) =>
                        handleAssociatedFieldChange(statementIndex, "taxpayerIdentificationNumber", value)
                    }
                    label="TIN"
                    isImportant
                    getFieldConfidence={getFieldConfidence}
                />
            </Box>
            <Box sx={{ flex: 1 }}>
                <TextField
                    select
                    size="small"
                    fullWidth
                    label="TIN Type"
                    value={statement.associated?.tinType || ""}
                    onChange={(e) =>
                        handleAssociatedFieldChange(statementIndex, "tinType", e.target.value || undefined)
                    }
                    error={getLowConfidence(`${fieldPath}[${statementIndex}].associated.tinType`)}
                >
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value="ssn">SSN</MenuItem>
                    <MenuItem value="ein">EIN</MenuItem>
                </TextField>
            </Box>
            <Box sx={{ flex: 2 }}>
                <FormField
                    fieldPath={`${fieldPath}[${statementIndex}].associated.name`}
                    value={statement.associated?.name || ""}
                    handleFieldChange={(_, value) => handleAssociatedFieldChange(statementIndex, "name", value)}
                    label="Name"
                    isImportant
                    lowConfidence={getLowConfidence(`${fieldPath}[${statementIndex}].associated.name`)}
                />
            </Box>
        </Box>
    );

    const renderGenericStatement = (statement: GenericStatementData, statementIndex: number) => (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2">Statement Data</Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddDataItem(statementIndex)}
                    size="small"
                    variant="outlined"
                >
                    Add Item
                </Button>
            </Box>

            {statement.data && statement.data.length === 0 ? (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontStyle: "italic" }}
                >
                    No data items. Click &quot;Add Item&quot; to add statement entries.
                </Typography>
            ) : (
                statement.data?.map((item: GenericDataItem, dataIndex: number) => {
                    const nonRecurring = isNonRecurring(item.cashFlowCategory);
                    return (
                        <Box
                            key={dataIndex}
                            sx={{
                                mb: 1.5,
                                p: 1.5,
                                border: `${nonRecurring ? 2 : 1}px solid ${
                                    nonRecurring ? theme.palette.warning.main : theme.palette.divider
                                }`,
                                borderRadius: 1,
                                backgroundColor: nonRecurring
                                    ? alpha(theme.palette.warning.main, 0.08)
                                    : theme.palette.mode === "dark"
                                      ? alpha(theme.palette.background.paper, 0.5)
                                      : theme.palette.grey[50],
                            }}
                        >
                            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                                <Box sx={{ flex: 2 }}>
                                    <FormField
                                        fieldPath={`${fieldPath}[${statementIndex}].data[${dataIndex}].key`}
                                        value={item.key || ""}
                                        handleFieldChange={(_, value) =>
                                            handleDataItemFieldChange(statementIndex, dataIndex, "key", value)
                                        }
                                        label="Key"
                                        lowConfidence={getLowConfidence(
                                            `${fieldPath}[${statementIndex}].data[${dataIndex}].key`,
                                        )}
                                    />
                                </Box>
                                <Box sx={{ flex: 3 }}>
                                    <FormField
                                        fieldPath={`${fieldPath}[${statementIndex}].data[${dataIndex}].name`}
                                        value={item.name || ""}
                                        handleFieldChange={(_, value) =>
                                            handleDataItemFieldChange(statementIndex, dataIndex, "name", value)
                                        }
                                        label="Description"
                                        lowConfidence={getLowConfidence(
                                            `${fieldPath}[${statementIndex}].data[${dataIndex}].name`,
                                        )}
                                        isImportant
                                    />
                                </Box>
                                <Box sx={{ flex: 2 }}>
                                    <FormField
                                        fieldPath={`${fieldPath}[${statementIndex}].data[${dataIndex}].value`}
                                        value={item.value ?? ""}
                                        handleFieldChange={(_, value) =>
                                            handleDataItemFieldChange(
                                                statementIndex,
                                                dataIndex,
                                                "value",
                                                Number(value) || 0,
                                            )
                                        }
                                        label="Value"
                                        type="currency"
                                        lowConfidence={getLowConfidence(
                                            `${fieldPath}[${statementIndex}].data[${dataIndex}].value`,
                                        )}
                                        isImportant
                                    />
                                </Box>
                                <IconButton
                                    onClick={() => handleRemoveDataItem(statementIndex, dataIndex)}
                                    color="error"
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: "flex", gap: 2, mt: 1, alignItems: "center" }}>
                                <Box sx={{ flex: 1, maxWidth: 320 }}>
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        label="Cash Flow Category"
                                        value={item.cashFlowCategory || "unclassified"}
                                        onChange={(e) =>
                                            handleDataItemFieldChange(
                                                statementIndex,
                                                dataIndex,
                                                "cashFlowCategory",
                                                e.target.value as CashFlowCategory,
                                            )
                                        }
                                        error={getLowConfidence(
                                            `${fieldPath}[${statementIndex}].data[${dataIndex}].cashFlowCategory`,
                                        )}
                                    >
                                        {CASH_FLOW_CATEGORY_OPTIONS.map((opt) => (
                                            <MenuItem
                                                key={opt.value}
                                                value={opt.value}
                                            >
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                {nonRecurring && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 1,
                                            backgroundColor: alpha(theme.palette.warning.main, 0.15),
                                            color: theme.palette.warning.dark,
                                            fontWeight: 600,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Non-Recurring
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })
            )}
        </Box>
    );

    const renderWagesStatement = (statement: WagesStatementData, statementIndex: number) => (
        <Box sx={{ mt: 2 }}>
            {/* Form Reference */}
            <Box sx={{ mb: 2 }}>
                <FormField
                    fieldPath={`${fieldPath}[${statementIndex}].formReference`}
                    value={statement.formReference || ""}
                    handleFieldChange={(_, value) => handleStatementFieldChange(statementIndex, "formReference", value)}
                    label="Form Reference"
                    lowConfidence={getLowConfidence(`${fieldPath}[${statementIndex}].formReference`)}
                />
            </Box>

            {/* Employers */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2">Employers</Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleAddEmployer(statementIndex)}
                    size="small"
                    variant="outlined"
                >
                    Add Employer
                </Button>
            </Box>

            {statement.employers && statement.employers.length === 0 ? (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontStyle: "italic" }}
                >
                    No employers. Click &quot;Add Employer&quot; to begin.
                </Typography>
            ) : (
                statement.employers?.map((employer: WagesEmployer, empIndex: number) => (
                    <Box
                        key={empIndex}
                        sx={{
                            mb: 1.5,
                            p: 1.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? alpha(theme.palette.background.paper, 0.5)
                                    : theme.palette.grey[50],
                        }}
                    >
                        <Box sx={{ display: "flex", gap: 2, mb: 1, alignItems: "flex-start" }}>
                            <Box sx={{ flex: 3 }}>
                                <FormField
                                    fieldPath={`${fieldPath}[${statementIndex}].employers[${empIndex}].employerName`}
                                    value={employer.employerName || ""}
                                    handleFieldChange={(_, value) =>
                                        handleEmployerFieldChange(statementIndex, empIndex, "employerName", value)
                                    }
                                    label="Employer Name"
                                    lowConfidence={getLowConfidence(
                                        `${fieldPath}[${statementIndex}].employers[${empIndex}].employerName`,
                                    )}
                                    isImportant
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    label="T/S"
                                    value={employer.taxpayerSpouse || ""}
                                    onChange={(e) =>
                                        handleEmployerFieldChange(
                                            statementIndex,
                                            empIndex,
                                            "taxpayerSpouse",
                                            e.target.value || null,
                                        )
                                    }
                                >
                                    <MenuItem value="">-</MenuItem>
                                    <MenuItem value="T">Taxpayer</MenuItem>
                                    <MenuItem value="S">Spouse</MenuItem>
                                </TextField>
                            </Box>
                            <IconButton
                                onClick={() => handleRemoveEmployer(statementIndex, empIndex)}
                                color="error"
                                size="small"
                                sx={{ mt: 0.5 }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <FormField
                                    fieldPath={`${fieldPath}[${statementIndex}].employers[${empIndex}].amountPaid`}
                                    value={employer.amountPaid ?? ""}
                                    handleFieldChange={(_, value) =>
                                        handleEmployerFieldChange(
                                            statementIndex,
                                            empIndex,
                                            "amountPaid",
                                            value ? Number(value) : null,
                                        )
                                    }
                                    label="Amount Paid"
                                    type="currency"
                                    lowConfidence={getLowConfidence(
                                        `${fieldPath}[${statementIndex}].employers[${empIndex}].amountPaid`,
                                    )}
                                    isImportant
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormField
                                    fieldPath={`${fieldPath}[${statementIndex}].employers[${empIndex}].federalTaxWithheld`}
                                    value={employer.federalTaxWithheld ?? ""}
                                    handleFieldChange={(_, value) =>
                                        handleEmployerFieldChange(
                                            statementIndex,
                                            empIndex,
                                            "federalTaxWithheld",
                                            value ? Number(value) : null,
                                        )
                                    }
                                    label="Federal Tax"
                                    type="currency"
                                    lowConfidence={getLowConfidence(
                                        `${fieldPath}[${statementIndex}].employers[${empIndex}].federalTaxWithheld`,
                                    )}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormField
                                    fieldPath={`${fieldPath}[${statementIndex}].employers[${empIndex}].stateTaxWithheld`}
                                    value={employer.stateTaxWithheld ?? ""}
                                    handleFieldChange={(_, value) =>
                                        handleEmployerFieldChange(
                                            statementIndex,
                                            empIndex,
                                            "stateTaxWithheld",
                                            value ? Number(value) : null,
                                        )
                                    }
                                    label="State Tax"
                                    type="currency"
                                    lowConfidence={getLowConfidence(
                                        `${fieldPath}[${statementIndex}].employers[${empIndex}].stateTaxWithheld`,
                                    )}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <FormField
                                    fieldPath={`${fieldPath}[${statementIndex}].employers[${empIndex}].cityOrSdiTaxWithheld`}
                                    value={employer.cityOrSdiTaxWithheld ?? ""}
                                    handleFieldChange={(_, value) =>
                                        handleEmployerFieldChange(
                                            statementIndex,
                                            empIndex,
                                            "cityOrSdiTaxWithheld",
                                            value ? Number(value) : null,
                                        )
                                    }
                                    label="City/SDI Tax"
                                    type="currency"
                                    lowConfidence={getLowConfidence(
                                        `${fieldPath}[${statementIndex}].employers[${empIndex}].cityOrSdiTaxWithheld`,
                                    )}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormField
                                    fieldPath={`${fieldPath}[${statementIndex}].employers[${empIndex}].ficaTax`}
                                    value={employer.ficaTax ?? ""}
                                    handleFieldChange={(_, value) =>
                                        handleEmployerFieldChange(
                                            statementIndex,
                                            empIndex,
                                            "ficaTax",
                                            value ? Number(value) : null,
                                        )
                                    }
                                    label="FICA Tax"
                                    type="currency"
                                    lowConfidence={getLowConfidence(
                                        `${fieldPath}[${statementIndex}].employers[${empIndex}].ficaTax`,
                                    )}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormField
                                    fieldPath={`${fieldPath}[${statementIndex}].employers[${empIndex}].medicareTax`}
                                    value={employer.medicareTax ?? ""}
                                    handleFieldChange={(_, value) =>
                                        handleEmployerFieldChange(
                                            statementIndex,
                                            empIndex,
                                            "medicareTax",
                                            value ? Number(value) : null,
                                        )
                                    }
                                    label="Medicare Tax"
                                    type="currency"
                                    lowConfidence={getLowConfidence(
                                        `${fieldPath}[${statementIndex}].employers[${empIndex}].medicareTax`,
                                    )}
                                />
                            </Box>
                        </Box>
                    </Box>
                ))
            )}

            {/* Totals */}
            <Typography
                variant="subtitle2"
                sx={{ mt: 2, mb: 1 }}
            >
                Totals
            </Typography>
            <Box
                sx={{
                    p: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    backgroundColor:
                        theme.palette.mode === "dark"
                            ? alpha(theme.palette.background.paper, 0.5)
                            : theme.palette.grey[50],
                }}
            >
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <FormField
                            fieldPath={`${fieldPath}[${statementIndex}].totals.amountPaid`}
                            value={statement.totals?.amountPaid ?? ""}
                            handleFieldChange={(_, value) =>
                                handleTotalsFieldChange(statementIndex, "amountPaid", value ? Number(value) : null)
                            }
                            label="Amount Paid"
                            type="currency"
                            lowConfidence={getLowConfidence(`${fieldPath}[${statementIndex}].totals.amountPaid`)}
                            isImportant
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <FormField
                            fieldPath={`${fieldPath}[${statementIndex}].totals.federalTaxWithheld`}
                            value={statement.totals?.federalTaxWithheld ?? ""}
                            handleFieldChange={(_, value) =>
                                handleTotalsFieldChange(
                                    statementIndex,
                                    "federalTaxWithheld",
                                    value ? Number(value) : null,
                                )
                            }
                            label="Federal Tax"
                            type="currency"
                            lowConfidence={getLowConfidence(
                                `${fieldPath}[${statementIndex}].totals.federalTaxWithheld`,
                            )}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <FormField
                            fieldPath={`${fieldPath}[${statementIndex}].totals.stateTaxWithheld`}
                            value={statement.totals?.stateTaxWithheld ?? ""}
                            handleFieldChange={(_, value) =>
                                handleTotalsFieldChange(
                                    statementIndex,
                                    "stateTaxWithheld",
                                    value ? Number(value) : null,
                                )
                            }
                            label="State Tax"
                            type="currency"
                            lowConfidence={getLowConfidence(`${fieldPath}[${statementIndex}].totals.stateTaxWithheld`)}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <FormField
                            fieldPath={`${fieldPath}[${statementIndex}].totals.cityOrSdiTaxWithheld`}
                            value={statement.totals?.cityOrSdiTaxWithheld ?? ""}
                            handleFieldChange={(_, value) =>
                                handleTotalsFieldChange(
                                    statementIndex,
                                    "cityOrSdiTaxWithheld",
                                    value ? Number(value) : null,
                                )
                            }
                            label="City/SDI Tax"
                            type="currency"
                            lowConfidence={getLowConfidence(
                                `${fieldPath}[${statementIndex}].totals.cityOrSdiTaxWithheld`,
                            )}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <FormField
                            fieldPath={`${fieldPath}[${statementIndex}].totals.ficaTax`}
                            value={statement.totals?.ficaTax ?? ""}
                            handleFieldChange={(_, value) =>
                                handleTotalsFieldChange(statementIndex, "ficaTax", value ? Number(value) : null)
                            }
                            label="FICA Tax"
                            type="currency"
                            lowConfidence={getLowConfidence(`${fieldPath}[${statementIndex}].totals.ficaTax`)}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <FormField
                            fieldPath={`${fieldPath}[${statementIndex}].totals.medicareTax`}
                            value={statement.totals?.medicareTax ?? ""}
                            handleFieldChange={(_, value) =>
                                handleTotalsFieldChange(statementIndex, "medicareTax", value ? Number(value) : null)
                            }
                            label="Medicare Tax"
                            type="currency"
                            lowConfidence={getLowConfidence(`${fieldPath}[${statementIndex}].totals.medicareTax`)}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">{title}</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => handleAddStatement("generic")}
                        size="medium"
                        variant="contained"
                        color="primary"
                    >
                        Add Statement
                    </Button>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => handleAddStatement("wagesReceived")}
                        size="medium"
                        variant="outlined"
                        color="primary"
                    >
                        Add Wages Statement
                    </Button>
                </Box>
            </Box>

            {statements.length === 0 ? (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, fontStyle: "italic" }}
                >
                    No statements. Click &quot;Add Statement&quot; to begin.
                </Typography>
            ) : (
                statements.map((statement, statementIndex) => (
                    <Box
                        key={statementIndex}
                        sx={{
                            mb: 3,
                            p: 2,
                            border: `2px solid ${
                                statement.statementSubtype === "wagesReceived"
                                    ? theme.palette.secondary.main
                                    : theme.palette.primary.main
                            }`,
                            borderRadius: 2,
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? alpha(theme.palette.background.paper, 0.6)
                                    : alpha(theme.palette.primary.main, 0.02),
                        }}
                    >
                        {/* Statement Header */}
                        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    backgroundColor:
                                        statement.statementSubtype === "wagesReceived"
                                            ? alpha(theme.palette.secondary.main, 0.1)
                                            : alpha(theme.palette.primary.main, 0.1),
                                    color:
                                        statement.statementSubtype === "wagesReceived"
                                            ? theme.palette.secondary.main
                                            : theme.palette.primary.main,
                                    mt: 1,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {statement.statementSubtype === "wagesReceived" ? "Wages" : "Generic"}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                                <FormField
                                    fieldPath={`${fieldPath}[${statementIndex}].title`}
                                    value={statement.title || ""}
                                    handleFieldChange={(_, value) =>
                                        handleStatementFieldChange(statementIndex, "title", value)
                                    }
                                    label="Statement Title"
                                    lowConfidence={getLowConfidence(`${fieldPath}[${statementIndex}].title`)}
                                    isImportant
                                />
                            </Box>
                            <IconButton
                                onClick={() => handleRemoveStatement(statementIndex)}
                                color="error"
                                size="small"
                                sx={{ mt: 0.5 }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>

                        {/* Associated Metadata */}
                        {renderAssociatedFields(statement, statementIndex)}

                        {/* Subtype-specific content */}
                        {statement.statementSubtype === "wagesReceived"
                            ? renderWagesStatement(statement, statementIndex)
                            : renderGenericStatement(statement as GenericStatementData, statementIndex)}
                    </Box>
                ))
            )}
        </Box>
    );
};

export default FormFieldStatementSections;
